import { FC, ReactNode, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  CircularProgress,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  BoltRounded,
  CalendarMonthRounded,
  ExpandMoreRounded,
  InfoOutlined,
  TaskAltRounded,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import './TeamAcceptInviteModal.scss';
import { AppSwitch, BasicDialog } from '../../common';
import TeamRoleChip from '../TeamRoleChip';

type AcceptSection = 'calendar' | 'tasks' | 'energy';

interface TeamAcceptInviteOption {
  id: string;
  name: string;
  provider: string;
  color?: string;
}

interface TeamAcceptInvitePreviewMember {
  userId: string;
  name: string;
  photoUrl: string;
}

interface TeamAcceptInviteModalProps {
  open: boolean;
  teamName: string;
  inviteRole?: 'admin' | 'member';
  inviterName?: string;
  previewMembers?: TeamAcceptInvitePreviewMember[];
  memberSummaryText?: string;
  isViewerEligible: boolean;
  blockedReason?: string;
  calendarOptions: TeamAcceptInviteOption[];
  taskBoardOptions: TeamAcceptInviteOption[];
  selectedCalendarIds: string[];
  selectedTaskBoardIds: string[];
  shareEnergy: boolean;
  isLoadingInvite: boolean;
  isLoadingCalendars: boolean;
  isLoadingTaskBoards: boolean;
  isAcceptingInvite: boolean;
  inviteError: string | null;
  acceptError: string | null;
  onToggleCalendar: (calendarId: string) => void;
  onToggleTaskBoard: (taskBoardId: string) => void;
  onToggleShareEnergy: () => void;
  onAccept: () => void;
  onDecline: () => void;
}

const TeamAcceptInviteModal: FC<TeamAcceptInviteModalProps> = ({
  open,
  teamName,
  inviteRole = 'member',
  inviterName = '',
  previewMembers = [],
  memberSummaryText = '',
  isViewerEligible,
  blockedReason = '',
  calendarOptions,
  taskBoardOptions,
  selectedCalendarIds,
  selectedTaskBoardIds,
  shareEnergy,
  isLoadingInvite,
  isLoadingCalendars,
  isLoadingTaskBoards,
  isAcceptingInvite,
  inviteError,
  acceptError,
  onToggleCalendar,
  onToggleTaskBoard,
  onToggleShareEnergy,
  onAccept,
  onDecline,
}) => {
  const { t } = useTranslation();
  const [expandedSection, setExpandedSection] = useState<AcceptSection | null>('calendar');

  const hasResolvedTeamName = Boolean(teamName?.trim());
  const shouldShowTeamNameShimmer = !hasResolvedTeamName;
  const resolvedTeamName = teamName?.trim() || t('team.common.team');
  const resolvedInviterName = inviterName.trim();
  const teamInitials = resolvedTeamName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((token) => token[0]?.toUpperCase() || '')
    .join('');
  const previewTeamMembers = previewMembers.slice(0, 4);
  const acceptedCalendarCount = selectedCalendarIds.length;
  const acceptedTaskBoardCount = selectedTaskBoardIds.length;
  const isInviteBlocked = !isViewerEligible;
  const blockedCopy = t('team.acceptInvite.blockedCopy');

  const isAcceptDisabled =
    isLoadingInvite ||
    isLoadingCalendars ||
    isLoadingTaskBoards ||
    isInviteBlocked ||
    Boolean(inviteError);

  const actions = isInviteBlocked ? (
    <div className="team-accept-invite-modal__actions team-accept-invite-modal__actions--blocked">
      <Button
        type="button"
        variant="outlined"
        className="team-page__button team-page__button--secondary team-accept-invite-modal__action"
        onClick={onDecline}
      >
        <Typography variant="body2" component="span">
          {t('team.common.close')}
        </Typography>
      </Button>
    </div>
  ) : (
    <div className="team-accept-invite-modal__actions">
      <Button
        type="button"
        variant="outlined"
        className="team-page__button team-page__button--secondary team-accept-invite-modal__action"
        disabled={isAcceptingInvite}
        onClick={onDecline}
      >
        <Typography variant="body2" component="span">
          {t('team.common.cancel')}
        </Typography>
      </Button>
      <LoadingButton
        type="button"
        variant="contained"
        loading={isAcceptingInvite}
        disabled={isAcceptingInvite || isAcceptDisabled}
        className="team-page__button team-page__button--primary team-accept-invite-modal__action"
        onClick={onAccept}
      >
        <Typography variant="body2" component="span">
          {t('team.acceptInvite.acceptInvite')}
        </Typography>
      </LoadingButton>
    </div>
  );

  const renderOptionList = (
    options: TeamAcceptInviteOption[],
    selectedIds: string[],
    isLoading: boolean,
    emptyCopy: string,
    onToggle: (id: string) => void
  ): ReactNode => {
    if (isLoading) {
      return (
        <Typography variant="body2" component="p" className="team-accept-invite-modal__empty-row">
          {t('team.common.loading')}
        </Typography>
      );
    }

    if (!options.length) {
      return (
        <Typography variant="body2" component="p" className="team-accept-invite-modal__empty-row">
          {emptyCopy}
        </Typography>
      );
    }

    return options.map((option) => {
      return (
        <label key={option.id} className="team-accept-invite-modal__option">
          <Checkbox
            checked={selectedIds.includes(option.id)}
            className="team-accept-invite-modal__checkbox"
            onChange={() => onToggle(option.id)}
          />
          {option.color && (
            <span
              aria-hidden="true"
              className="team-accept-invite-modal__calendar-dot"
              style={{ background: option.color }}
            />
          )}
          <div className="team-accept-invite-modal__option-copy">
            <Typography
              variant="body2"
              component="span"
              className="team-accept-invite-modal__option-title"
            >
              {option.name}
            </Typography>
            <Typography
              variant="subtitle2"
              component="span"
              className="team-accept-invite-modal__option-meta"
            >
              {option.provider}
            </Typography>
          </div>
        </label>
      );
    });
  };

  return (
    <BasicDialog
      visible={open}
      title=""
      className="team-accept-invite-modal-dialog"
      actions={actions}
      onYes={isInviteBlocked ? onDecline : onAccept}
      onClose={onDecline}
    >
      <div className="team-accept-invite-modal" aria-live="polite">
        <header className="team-accept-invite-modal__hero">
          <div className="team-accept-invite-modal__team-avatar">{teamInitials || 'TM'}</div>
          <Typography
            variant="body2"
            component="p"
            className="team-accept-invite-modal__invite-line"
          >
            {resolvedInviterName
              ? t('team.acceptInvite.invitedBy', { inviterName: resolvedInviterName })
              : t('team.acceptInvite.invitedGeneric')}
          </Typography>
          <Typography variant="h2" component="h1" className="team-accept-invite-modal__team-name">
            {shouldShowTeamNameShimmer ? (
              <span aria-hidden="true" className="team-accept-invite-modal__team-name-shimmer" />
            ) : (
              resolvedTeamName
            )}
          </Typography>
          <TeamRoleChip
            role={inviteRole}
            uppercase
            pill
            className="team-accept-invite-modal__role-chip"
          />
        </header>

        {(memberSummaryText || previewTeamMembers.length > 0) && (
          <div className="team-accept-invite-modal__member-banner">
            {!!previewTeamMembers.length && (
              <div className="team-accept-invite-modal__member-stack" aria-hidden="true">
                {previewTeamMembers.map((member) => {
                  const memberInitials = member.name
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((nameToken) => nameToken[0]?.toUpperCase() || '')
                    .join('');

                  return (
                    <span
                      key={member.userId}
                      className="team-accept-invite-modal__member-avatar"
                      title={member.name || t('team.acceptInvite.teamMember')}
                    >
                      {member.photoUrl ? (
                        <img src={member.photoUrl} alt="" />
                      ) : (
                        memberInitials || 'TM'
                      )}
                    </span>
                  );
                })}
              </div>
            )}
            <Typography
              variant="body2"
              component="p"
              className="team-accept-invite-modal__member-copy"
            >
              {memberSummaryText || t('team.acceptInvite.teammatesAlreadyHere')}
            </Typography>
          </div>
        )}

        <Typography variant="body2" component="p" className="team-accept-invite-modal__share-intro">
          {isInviteBlocked
            ? t('team.acceptInvite.inviteCannotBeUsed')
            : t('team.acceptInvite.shareIntro')}
        </Typography>

        {inviteError && (
          <Typography variant="body2" component="p" className="team-accept-invite-modal__error">
            {inviteError}
          </Typography>
        )}

        {isLoadingInvite ? (
          <div className="team-accept-invite-modal__loading">
            <CircularProgress size={20} className="team-accept-invite-modal__spinner" />
            <Typography
              variant="body2"
              component="p"
              className="team-accept-invite-modal__loading-copy"
            >
              {t('team.acceptInvite.loadingDetails')}
            </Typography>
          </div>
        ) : isInviteBlocked ? (
          <div
            className="team-accept-invite-modal__blocked-box"
            role="status"
            data-blocked-reason={blockedReason || undefined}
          >
            <span className="team-accept-invite-modal__blocked-icon" aria-hidden="true">
              <InfoOutlined />
            </span>
            <Typography
              variant="body2"
              component="p"
              className="team-accept-invite-modal__blocked-copy"
            >
              {blockedCopy}
            </Typography>
          </div>
        ) : !inviteError ? (
          <div className="team-accept-invite-modal__accordion-list">
            <Accordion
              disableGutters
              elevation={0}
              expanded={expandedSection === 'calendar'}
              className="team-accept-invite-modal__accordion"
              onChange={(_, isExpanded) => {
                setExpandedSection(isExpanded ? 'calendar' : null);
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreRounded />}
                className="team-accept-invite-modal__accordion-summary"
              >
                <span className="team-accept-invite-modal__summary-icon">
                  <CalendarMonthRounded />
                </span>
                <div className="team-accept-invite-modal__summary-copy">
                  <Typography
                    variant="body2"
                    component="span"
                    className="team-accept-invite-modal__summary-title"
                  >
                    {t('team.acceptInvite.calendarEvents')}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    component="span"
                    className="team-accept-invite-modal__summary-meta"
                  >
                    {t('team.common.selectedCount', { count: acceptedCalendarCount })}
                  </Typography>
                </div>
              </AccordionSummary>
              <AccordionDetails className="team-accept-invite-modal__accordion-details">
                {renderOptionList(
                  calendarOptions,
                  selectedCalendarIds,
                  isLoadingCalendars,
                  t('team.common.noCalendarsFound'),
                  onToggleCalendar
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion
              disableGutters
              elevation={0}
              expanded={expandedSection === 'tasks'}
              className="team-accept-invite-modal__accordion"
              onChange={(_, isExpanded) => {
                setExpandedSection(isExpanded ? 'tasks' : null);
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreRounded />}
                className="team-accept-invite-modal__accordion-summary"
              >
                <span className="team-accept-invite-modal__summary-icon">
                  <TaskAltRounded />
                </span>
                <div className="team-accept-invite-modal__summary-copy">
                  <Typography
                    variant="body2"
                    component="span"
                    className="team-accept-invite-modal__summary-title"
                  >
                    {t('team.acceptInvite.taskBoards')}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    component="span"
                    className="team-accept-invite-modal__summary-meta"
                  >
                    {t('team.common.selectedCount', { count: acceptedTaskBoardCount })}
                  </Typography>
                </div>
              </AccordionSummary>
              <AccordionDetails className="team-accept-invite-modal__accordion-details">
                {renderOptionList(
                  taskBoardOptions,
                  selectedTaskBoardIds,
                  isLoadingTaskBoards,
                  t('team.common.noTaskBoardsFound'),
                  onToggleTaskBoard
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion
              disableGutters
              elevation={0}
              expanded={expandedSection === 'energy'}
              className="team-accept-invite-modal__accordion"
              onChange={(_, isExpanded) => {
                setExpandedSection(isExpanded ? 'energy' : null);
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreRounded />}
                className="team-accept-invite-modal__accordion-summary"
              >
                <span className="team-accept-invite-modal__summary-icon">
                  <BoltRounded />
                </span>
                <div className="team-accept-invite-modal__summary-copy">
                  <Typography
                    variant="body2"
                    component="span"
                    className="team-accept-invite-modal__summary-title"
                  >
                    {t('team.acceptInvite.energyWindows')}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    component="span"
                    className="team-accept-invite-modal__summary-meta"
                  >
                    {shareEnergy ? t('team.common.shared') : t('team.common.private')}
                  </Typography>
                </div>
              </AccordionSummary>
              <AccordionDetails className="team-accept-invite-modal__accordion-details">
                <div className="team-accept-invite-modal__energy-row">
                  <div className="team-accept-invite-modal__energy-copy">
                    <Typography
                      variant="body2"
                      component="span"
                      className="team-accept-invite-modal__energy-title"
                    >
                      {t('team.acceptInvite.shareEnergyTrend')}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      component="span"
                      className="team-accept-invite-modal__energy-description"
                    >
                      {t('team.acceptInvite.shareEnergyDescription')}
                    </Typography>
                  </div>
                  <AppSwitch checked={shareEnergy} onChange={onToggleShareEnergy} />
                </div>
              </AccordionDetails>
            </Accordion>
          </div>
        ) : null}

        {!inviteError && !isLoadingInvite && !isInviteBlocked && (
          <div className="team-accept-invite-modal__helper-box" role="note">
            <span className="team-accept-invite-modal__helper-icon" aria-hidden="true">
              <InfoOutlined />
            </span>
            <Typography
              variant="subtitle2"
              component="p"
              className="team-accept-invite-modal__helper"
            >
              {t('team.acceptInvite.updateLater')}
            </Typography>
          </div>
        )}

        {acceptError && !isInviteBlocked && (
          <Typography variant="body2" component="p" className="team-accept-invite-modal__error">
            {acceptError}
          </Typography>
        )}
      </div>
    </BasicDialog>
  );
};

export default TeamAcceptInviteModal;
